import { Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { supabase } from './supabase';
import { Session } from '@supabase/supabase-js';
import { AdminOverview } from './pages/AdminOverview';
import { AdminStudents } from './pages/AdminStudents';
import { AdminParents } from './pages/AdminParents';
import { AdminPrograms } from './pages/AdminPrograms';
import { AdminFinances } from './pages/AdminFinances';
import { AdminEnrollments } from './pages/AdminEnrollments';
import { AdminCoupons } from './pages/AdminCoupons';
import AdminAnalytics from './pages/AdminAnalytics';
import AdminInsights from './pages/AdminInsights';
import { AdminEmail } from './pages/AdminEmail';
import { AdminSignups } from './pages/AdminSignups';
import { AdminSignupDetails } from './pages/AdminSignupDetails';
import AdminSystem from './pages/AdminSystem';
import { AdminTeachers } from './pages/AdminTeachers';
import { AdminAgentSearch } from './pages/AdminAgentSearch';
import { AdminBalances } from './pages/AdminBalances';
import AdminFacilityRent from './pages/AdminFacilityRent';
import AdminClosures from './pages/AdminClosures';

interface AdminUser {
  id: string;
  email: string;
  created_at: string;
}

export function AdminApp() {
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const [unauthorizedAccess, setUnauthorizedAccess] = useState(false);

  useEffect(() => {
    let mounted = true;

    async function checkSession() {
      try {
        setLoading(true);
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          throw sessionError;
        }

        if (!mounted) return;

        setSession(session);

        if (session) {
          // Fetch admin user if we have a session
          const { data, error: adminError } = await supabase
            .from('admin_users')
            .select('*')
            .limit(1)
            .single();

          if (adminError) {
            // Check if it's a 406 Not Acceptable error (no rows returned)
            if (adminError.code === 'PGRST116' || adminError.message.includes('No rows returned') || adminError.message.includes('406')) {
              setUnauthorizedAccess(true);
              setLoading(false);
              return;
            }
            throw adminError;
          }

          if (!mounted) return;
          setAdminUser(data);
        }
        
        setLoading(false);
      } catch (err: unknown) {
        if (!mounted) return;
        console.error('Error checking session:', err);
        
        // Check for specific error codes related to unauthorized access
        const errorObj = err as { status?: number; code?: string; message?: string };
        if (errorObj.status === 406 || errorObj.code === 'PGRST116' || (errorObj.message && errorObj.message.includes('406'))) {
          setUnauthorizedAccess(true);
        } else {
          setError(errorObj.message || 'An unknown error occurred');
        }
        setLoading(false);
      }
    }

    checkSession();

    return () => {
      mounted = false;
    };
  }, []);


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <div className="text-xl text-gray-600">Loading...</div>
        </div>
      </div>
    );
  }

  if (unauthorizedAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center p-8 bg-white shadow-lg rounded-lg max-w-md">
          <div className="flex justify-center mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-6">Your account does not have administrator privileges required to access this area.</p>
          <div className="flex flex-col space-y-3">
            <a
              href="/"
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors text-center block"
            >
              Return to Home Page
            </a>
            <button
              onClick={() => window.location.reload()}
              className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center p-4 bg-white shadow rounded-lg">
          <h2 className="text-xl text-red-600 mb-2">Error</h2>
          <p>{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!session || !adminUser) {
    return <Navigate to="/" replace />;
  }

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/admin-app/overview" replace />} />
      <Route path="/overview" element={<AdminOverview />} />
      <Route path="/students" element={<AdminStudents />} />
      <Route path="/parents" element={<AdminParents />} />
      <Route path="/teachers" element={<AdminTeachers />} />
      <Route path="/programs" element={<AdminPrograms />} />
      <Route path="/school-calendar" element={<AdminClosures />} />
      <Route path="/closures" element={<Navigate to="/admin-app/school-calendar" replace />} />
      <Route path="/finances" element={<AdminFinances />} />
      <Route path="/revenue-share" element={<AdminFacilityRent />} />
      <Route path="/facility-rent" element={<Navigate to="/admin-app/revenue-share" replace />} />
      <Route path="/enrollments" element={<AdminEnrollments />} />
      <Route path="/coupons" element={<AdminCoupons />} />
      <Route path="/analytics" element={<AdminAnalytics />} />
      <Route path="/insights" element={<AdminInsights />} />
      <Route path="/system" element={<AdminSystem />} />
      <Route path="/changelog" element={<AdminSystem />} />
      <Route path="/email" element={<AdminEmail />} />
      <Route path="/signups" element={<AdminSignups />} />
      <Route path="/signups/:eventId" element={<AdminSignupDetails />} />
      <Route path="/agent-search" element={<AdminAgentSearch />} />
      <Route path="/balances" element={<AdminBalances />} />
      <Route path="*" element={<Navigate to="/admin-app/overview" replace />} />
    </Routes>
  );
} 
